import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IdObject } from 'src/app/core/id-object';
import { Client, ClientDraft } from './client';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private static listUrl = (apiUrl: string): string => `${apiUrl}client`;
  private static singleUrl = (apiUrl: string, id: string): string =>
    `${apiUrl}client/${id}`;

  constructor(private httpClient: HttpClient) {}

  public getList(): Observable<Client[]> {
    const { apiUrl } = environment;
    const url = ClientService.listUrl(apiUrl);

    return this.httpClient
      .get<Client[]>(url)
      .pipe(
        catchError(this.handleError<Client[]>('ClientService->getList', [])),
      );
  }

  public getSingle(id: string): Observable<Client> {
    const { apiUrl } = environment;
    const url = ClientService.singleUrl(apiUrl, id);

    return this.httpClient
      .get<Client>(url)
      .pipe(
        catchError(
          this.handleError<Client>('ClientService->getSingle', undefined),
        ),
      );
  }

  public create(clientDraft: ClientDraft): Observable<IdObject> {
    const { apiUrl } = environment;
    const url = ClientService.listUrl(apiUrl);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.httpClient
      .post<IdObject>(url, clientDraft, { headers })
      .pipe(
        catchError(
          this.handleError<IdObject>('ClientService->create', undefined),
        ),
      );
  }

  public patch(id: string, clientDraft: ClientDraft): Observable<boolean> {
    const { apiUrl } = environment;
    const url = ClientService.singleUrl(apiUrl, id);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.httpClient
      .patch<boolean>(url, clientDraft, { headers })
      .pipe(
        catchError(this.handleError<boolean>('ClientService->patch', false)),
      );
  }

  public remove(id: string): Observable<boolean> {
    const { apiUrl } = environment;
    const url = ClientService.singleUrl(apiUrl, id);

    return this.httpClient.delete<void>(url).pipe(
      map(() => true),
      catchError(this.handleError('ClientService->remove', false)),
    );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(
    operation = 'operation',
    result?: T,
  ): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: `, error);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
